/**
 * What the patient's Confirm Details card needs about a plan member who is
 * someone else, before Continue — the one testable rule (planMemberConfirm.test.ts).
 *
 * Case imp_5585d00d: the card demanded the plan member's EMAIL, so a patient
 * whose plan member has no email, or who only knows their mobile, could never
 * continue — while the SMS channel to a plan member already existed. Now an
 * email OR a mobile reaches them: name, date of birth, and one way to send
 * their link. A box that is filled must still be valid. With only a mobile,
 * the physician texts the link (Parseo does not text a plan holder on its
 * own; the physician confirms their consent first) — the card says so.
 */

import { isFormatValid } from "../shared/lib/inputFormats";

export interface PlanMemberConfirmValues {
  plan_member_first_name?: string;
  plan_member_last_name?: string;
  plan_member_dob?: string;
  plan_member_email?: string;
  plan_member_phone?: string;
}

const filled = (s?: string) => String(s || "").trim() !== "";

/** The plan member can be reached: a valid email or a valid mobile (a filled
 *  box that is malformed fails, even when the other one is fine). */
export function planMemberReachable(v: PlanMemberConfirmValues): boolean {
  const email = String(v.plan_member_email || "").trim();
  const phone = String(v.plan_member_phone || "").trim();
  if (!email && !phone) return false;
  if (email && !isFormatValid("email", email)) return false;
  if (phone && !isFormatValid("phone_na", phone)) return false;
  return true;
}

/** Everything the plan member's own link needs from this card. */
export function planMemberDetailsComplete(v: PlanMemberConfirmValues): boolean {
  return filled(v.plan_member_first_name)
    && filled(v.plan_member_last_name)
    && filled(v.plan_member_dob)
    && planMemberReachable(v);
}

/** True when the only way to reach them is a text — the card tells the
 *  patient the office will send it. */
export function planMemberTextOnly(v: PlanMemberConfirmValues): boolean {
  return !filled(v.plan_member_email) && filled(v.plan_member_phone);
}
